import { useCallback, useEffect, useRef, useState } from 'react';
import { IRetryableError, isRetryableError } from 'constants/errors/codes';

export interface UsePollingOptions<T> {
  intervalMs: number;
  enabled?: boolean;
  // Fire the first call as soon as polling is enabled (default) instead of waiting one
  // interval. Ignored when `manualSchedule` is true.
  immediate?: boolean;
  // When true, the hook never schedules its own first tick on enable — the caller must call
  // `resetSchedule()` once it has its own fresh data. Use this when an independent fetch (e.g.
  // the initial load) already covers the same endpoint: without this, the poll's timer starts
  // counting from mount regardless of that fetch, so a slow initial load and the poll's first
  // tick can end up in flight at the same time — the exact overlap this hook exists to prevent.
  manualSchedule?: boolean;
  maxConsecutiveFailures?: number;
  backoffFactor?: number;
  maxBackoffMs?: number;
  isRetryable?: (error: unknown) => boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
}

export interface UsePollingResult<T> {
  data: T | null;
  error: unknown;
  isFetching: boolean;
  isCircuitOpen: boolean;
  retry: () => void;
  // Reschedules the next tick to fire one interval from now, without fetching immediately.
  // For use with `manualSchedule: true`, after an independent fetch to the same endpoint
  // completes, so the poll's next check is timed from that fetch instead of from mount.
  resetSchedule: () => void;
  // Cancels a pending (not yet fired) tick without scheduling a new one. Pair with
  // `resetSchedule()`: call this right before starting an independent fetch to the same
  // endpoint and `resetSchedule()` once it completes, so a tick already due to fire can't
  // still go out while that fetch is in flight.
  clearSchedule: () => void;
}

const defaultIsRetryable = (error: unknown): boolean => {
  const retryable = (error as { retryable?: unknown })?.retryable;
  return typeof retryable === 'boolean' ? retryable : isRetryableError(error as IRetryableError);
};

// Self-scheduling poller: the next call is only ever scheduled after the previous one
// resolved (success or failure), so two calls to `fetchFn` can never be in flight at once.
// On a retryable failure (5xx, timeout, network error) it backs off exponentially; after
// `maxConsecutiveFailures` in a row, or immediately on a non-retryable failure, it stops
// and exposes `retry()` as a manual resume.
export const usePolling = <T>(
  fetchFn: () => Promise<T>,
  {
    intervalMs,
    enabled = true,
    immediate = true,
    manualSchedule = false,
    maxConsecutiveFailures = 3,
    backoffFactor = 2,
    maxBackoffMs,
    isRetryable = defaultIsRetryable,
    onSuccess,
    onError,
  }: UsePollingOptions<T>
): UsePollingResult<T> => {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isCircuitOpen, setIsCircuitOpen] = useState(false);

  const fetchFnRef = useRef(fetchFn);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const isRetryableRef = useRef(isRetryable);
  useEffect(() => {
    fetchFnRef.current = fetchFn;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    isRetryableRef.current = isRetryable;
  });

  const isMountedRef = useRef(true);
  const runIdRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);
  const consecutiveFailuresRef = useRef(0);
  const pausedRef = useRef(false);
  // Mirrored so resetSchedule() can check it without taking it as a dependency, which would
  // change the callback's identity every time the circuit flips.
  const isCircuitOpenRef = useRef(false);

  const updateCircuitOpen = useCallback((open: boolean) => {
    isCircuitOpenRef.current = open;
    setIsCircuitOpen(open);
  }, []);

  // Default cap allows the backoff to actually grow exponentially through every step up to
  // (but not exceeding) what the last failure before the circuit opens would naturally need —
  // otherwise, e.g. with the default 3-failure ceiling, a flat `intervalMs * 2` cap would clip
  // growth after the very first retry (2x, 2x instead of 2x, 4x).
  const maxBackoffMsResolved =
    maxBackoffMs ?? intervalMs * Math.pow(backoffFactor, Math.max(0, maxConsecutiveFailures - 1));

  const clearScheduledTick = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearScheduledTick();
    };
  }, [clearScheduledTick]);

  const executeTick = useCallback(
    async (runId: number) => {
      if (!isMountedRef.current || runId !== runIdRef.current) return;

      if (typeof document !== 'undefined' && document.hidden) {
        pausedRef.current = true;
        return;
      }

      setIsFetching(true);
      try {
        const result = await fetchFnRef.current();
        if (!isMountedRef.current || runId !== runIdRef.current) return;

        consecutiveFailuresRef.current = 0;
        setIsFetching(false);
        setError(null);
        setData(result);
        onSuccessRef.current?.(result);
        // A resetSchedule() that landed while this tick was in flight has its own timer armed;
        // overwriting timeoutRef without clearing would orphan it and leave two chains running.
        clearScheduledTick();
        timeoutRef.current = window.setTimeout(() => executeTick(runId), intervalMs);
      } catch (err) {
        if (!isMountedRef.current || runId !== runIdRef.current) return;

        setIsFetching(false);
        setError(err);
        onErrorRef.current?.(err);

        if (!isRetryableRef.current(err)) {
          clearScheduledTick();
          updateCircuitOpen(true);
          return;
        }

        consecutiveFailuresRef.current += 1;
        if (consecutiveFailuresRef.current >= maxConsecutiveFailures) {
          clearScheduledTick();
          updateCircuitOpen(true);
          return;
        }

        const backoffDelay = Math.min(
          intervalMs * Math.pow(backoffFactor, consecutiveFailuresRef.current),
          maxBackoffMsResolved
        );
        clearScheduledTick();
        timeoutRef.current = window.setTimeout(() => executeTick(runId), backoffDelay);
      }
    },
    [
      intervalMs,
      maxConsecutiveFailures,
      backoffFactor,
      maxBackoffMsResolved,
      clearScheduledTick,
      updateCircuitOpen,
    ]
  );

  useEffect(() => {
    if (!enabled) {
      // Bump the run id so a request already in flight from before we were disabled is
      // ignored when it resolves — otherwise it would still pass executeTick's run-id check,
      // call onSuccess, and schedule another tick, letting polling resume on its own.
      runIdRef.current += 1;
      clearScheduledTick();
      setIsFetching(false);
      // If the caller's condition for polling at all goes away (e.g. nothing left to poll
      // for), any stale circuit-open state from a previous run is no longer meaningful —
      // clear it so a dead retry affordance doesn't linger with nothing to retry.
      updateCircuitOpen(false);
      return;
    }

    const runId = ++runIdRef.current;
    consecutiveFailuresRef.current = 0;
    pausedRef.current = false;
    updateCircuitOpen(false);

    if (manualSchedule) {
      // Wait for an explicit resetSchedule() call — see the option's doc comment.
    } else if (immediate) {
      executeTick(runId);
    } else {
      timeoutRef.current = window.setTimeout(() => executeTick(runId), intervalMs);
    }

    return () => {
      clearScheduledTick();
    };
  }, [
    enabled,
    immediate,
    manualSchedule,
    intervalMs,
    executeTick,
    clearScheduledTick,
    updateCircuitOpen,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const handleVisibilityChange = () => {
      if (!document.hidden && pausedRef.current && enabled && !isCircuitOpen) {
        pausedRef.current = false;
        clearScheduledTick();
        executeTick(runIdRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled, isCircuitOpen, executeTick, clearScheduledTick]);

  const retry = useCallback(() => {
    clearScheduledTick();
    consecutiveFailuresRef.current = 0;
    pausedRef.current = false;
    updateCircuitOpen(false);
    setError(null);
    const runId = ++runIdRef.current;
    executeTick(runId);
  }, [clearScheduledTick, executeTick, updateCircuitOpen]);

  // Deliberately does nothing while the circuit is open: an explicit fetch re-arming the poll
  // would resume hammering a dead endpoint with the failure count still maxed out. Only retry()
  // resumes from that state.
  const resetSchedule = useCallback(() => {
    if (!enabled || isCircuitOpenRef.current) return;
    clearScheduledTick();
    timeoutRef.current = window.setTimeout(() => executeTick(runIdRef.current), intervalMs);
  }, [enabled, intervalMs, clearScheduledTick, executeTick]);

  return {
    data,
    error,
    isFetching,
    isCircuitOpen,
    retry,
    resetSchedule,
    clearSchedule: clearScheduledTick,
  };
};

export default usePolling;
